/* eslint-disable camelcase */

import {Trans} from '@lingui/macro';
import React from 'react';

import {PredicateOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {computeIsGlobalNamespace, isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {CompanyFeaturesEnum} from '../../models/api/companyModel';
import {RuleBundleModel, RuleBundleModelStrict} from '../../models/api/ruleBundleModel';
import {RuleBundleTypesEnum} from '../../models/api/ruleCommonTypes';
import {
  RuleBaseModel,
  RuleBaseModelStrict,
  RuleCompoundConditionTypesEnum,
  RuleModel,
  RuleModelStrict,
  RulesNamespaceTypesEnum,
} from '../../models/api/ruleModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {BusinessHoursParametersEnum, DDHHMM, DelayParam, DelayParameter} from '../editors/commons/paramModel';
import {accountRules} from './accountRulesModel';
import {advancedRules, upgradedRules2021} from './advancedRulesModel';
import {RuleAction, RuleActionTypesEnum} from './ruleActionModel';
import {RuleCondition, RuleConditionTypesEnum} from './ruleConditionModel';
import {toDelayParameter} from './slaRuleModel';

/**
 * Assuming a rule, *containing a top level 'AND' node with 'OR' children
 * (or leaves conditions),
 *
 * Returns the corresponding array of 'OR' children conditions.
 *
 * (AND a b (OR c d) e) => [[a], [b], [c, d], [e]]
 */
export function splitANDconditions(rule: RuleModel): ReadonlyArray<RuleCondition[]> {
  const result: RuleCondition[][] = [];

  const [op, ...conditions] = rule.compoundConditions;
  // Bailout if top level operator is not 'AND'.
  if (op !== RuleCompoundConditionTypesEnum.AND) {
    return [];
  }

  conditions.forEach((conditionOrCompoundCondition) => {
    // ['or', cond1, cond2, cond3...] -> Push [cond1, cond2, cond3]
    if (Array.isArray(conditionOrCompoundCondition)) {
      const [, ...orConditions] = conditionOrCompoundCondition;
      return result.push(orConditions);
    }

    // Single condition on its own -> push [cond]
    return result.push([conditionOrCompoundCondition]);
  });

  return result;
}

function isGlobalRule(rule: RuleBaseModel) {
  return computeIsGlobalNamespace(rule.namespace);
}

export function isGlobalBundle(bundle: RuleBundleModel) {
  return bundle.rules.every((r) => isGlobalRule(r));
}

export function isActiveBundle(bundle: RuleBundleModel) {
  return bundle.rules.every((r) => r.active);
}

export function humanDuration(dayHHMM: DDHHMM) {
  let result = '';

  if (dayHHMM.days) {
    result += `${dayHHMM.days}d`;
  }

  if (dayHHMM.hh) {
    result += `${dayHHMM.hh}h`;
  }

  if (dayHHMM.mm) {
    result += `${dayHHMM.mm}m`;
  }

  return result;
}

export type SlaDelays = {
  delay?: Partial<DelayParameter>;
  business_hours?: BusinessHoursParametersEnum;
};

function isRegardlessBH(d: SlaDelays) {
  return d.business_hours === undefined || d.business_hours === 'regardless_bh';
}

export function isWithinBH(d: SlaDelays) {
  return d.business_hours === 'within_bh';
}

function getBhOrDefault(d: SlaDelays | undefined) {
  return d?.business_hours ?? 'regardless_bh';
}

export function isDelayDefined(partialDelay: Partial<DelayParameter>) {
  const delay = toDelayParameter(partialDelay);
  return Boolean(delay && delay.val > 0);
}

function equals(a: SlaDelays, b: SlaDelays): boolean {
  if (getBhOrDefault(a) !== getBhOrDefault(b)) {
    return false;
  }

  const insideA = toDelayParameter(a.delay);
  const insideB = toDelayParameter(b.delay);

  const insideEquals =
    (insideA === undefined && insideB === undefined) ||
    (insideA && insideB && DelayParameter.equals(insideA, insideB));

  return Boolean(insideEquals);
}

const emptyDelay: DelayParam = {};

export const SlaDelays = {
  empty: emptyDelay,
  equals,
  isRegardlessBH,
  isWithinBH,
  getBhOrDefault,
};

// Helper used to narrow a single type from a sum type with a discriminating `type` field.
type UnionMemberWithType<U, K> = U extends {type: K} ? U : never;

/**
 * Traverses `rule` conditions, looking for all conditions of a matching type.
 * Only looks 2 level deeps.
 */
export function findRuleConditions<CONDTYPE extends RuleConditionTypesEnum>(
  rule: RuleModel,
  conditionType: CONDTYPE,
): UnionMemberWithType<RuleCondition, CONDTYPE>[] {
  const result: UnionMemberWithType<RuleCondition, CONDTYPE>[] = [];

  rule.compoundConditions.forEach((el) => {
    if (isOperator(el)) {
      return;
    }

    if (Array.isArray(el)) {
      el.forEach((opOrCondition) => {
        if (isOperator(opOrCondition)) {
          return;
        }

        if (opOrCondition.type === conditionType) {
          result.push(opOrCondition as UnionMemberWithType<RuleCondition, CONDTYPE>);
        }
      });
      return;
    }

    if (el.type === conditionType) {
      result.push(el as UnionMemberWithType<RuleCondition, CONDTYPE>);
    }
  });

  return result;
}

export function findRuleCondition<CONDTYPE extends RuleConditionTypesEnum>(
  rule: RuleModel,
  conditionType: CONDTYPE,
): UnionMemberWithType<RuleCondition, CONDTYPE> | undefined {
  return (findRuleConditions(rule, conditionType) as UnionMemberWithType<RuleCondition, CONDTYPE>[])[0];
}

const isOperator = makeEnumTypeGuard(RuleCompoundConditionTypesEnum);

export function findRuleActions<ACTIONTYPE extends RuleActionTypesEnum>(
  rule: RuleBaseModel,
  actionType: ACTIONTYPE,
): UnionMemberWithType<RuleAction, ACTIONTYPE>[] {
  return rule.actions.filter((action) => action.type === actionType) as UnionMemberWithType<
    RuleAction,
    ACTIONTYPE
  >[];
}

export function findRuleAction<ACTIONTYPE extends RuleActionTypesEnum>(
  rule: RuleBaseModel,
  actionType: ACTIONTYPE,
): UnionMemberWithType<RuleAction, ACTIONTYPE> | undefined {
  return findRuleActions(rule, actionType)[0];
}

export function computeIsRuleRecipeBundle(ruleBundleType: RuleBundleTypesEnum | undefined): boolean {
  return (
    ruleBundleType === RuleBundleTypesEnum.SLA ||
    ruleBundleType === RuleBundleTypesEnum.CSAT ||
    ruleBundleType === RuleBundleTypesEnum.REQUIRED_TAGGING
  );
}

export function isLoadBalancingRule(rule: RuleBaseModel): boolean {
  const randomAssignActions = findRuleActions(rule, RuleActionTypesEnum.RANDOM_ASSIGN);
  return randomAssignActions.some((action) => action.params.algo === 'load_balancing');
}

export function hasUnauthorizedAdvancedRule(
  hasAdvancedRules: boolean,
  hasUpgradedRules: boolean,
  bundle: RuleBundleModelStrict,
) {
  /*
   *  After the 2021 starter rule access upgrade, customers may be able to create rules
   *  that were formerly basic, but are now considered advanced. If the customer does not
   *  have a plan with access to advanced rules, this resulted in the rule being immediately
   *  disabled after creation with no warning or alert to the customer. This check sits in
   *  front of a confirm dialog that is intended to make the customer understand that their
   *  rule selection is disallowed and gives them the option to contact sales for an upgrade.
   *
   *  This is not intended as a long term fix since the user experience is still not good,
   *  but it should buy some time until a proper fix that disallows the creation of advanced
   *  rules can be made.
   */
  return !hasAdvancedRules && bundle.rules.some((rule) => isAdvancedForCompany(hasUpgradedRules, rule));
}

interface RuleLike
  extends Pick<RuleBaseModelStrict, 'events' | 'actions' | 'compoundConditions' | 'expressions'> {}

const FEATURE_PREDICATES: Partial<Record<CompanyFeaturesEnum, PredicateOf<RuleLike>>> = {
  [CompanyFeaturesEnum.ACCOUNT_RULES]: (rule) =>
    containsAccountActions(rule) || containsAccountConditions(rule),
  [CompanyFeaturesEnum.UPGRADED_RULES]: (rule) =>
    containsAdvancedEventsPre2021(rule) ||
    containsAdvancedActionsPre2021(rule) ||
    containsAdvancedCompoundConditionsPre2021(rule),
  [CompanyFeaturesEnum.ADVANCED_RULES]: (rule) =>
    containsAdvancedEvents(rule) || containsAdvancedActions(rule) || containsAdvancedCompoundConditions(rule),
  [CompanyFeaturesEnum.SMART_RULES]: (rule) => containsRuleExpressions(rule),
};

export function getRequiredFeaturesForRule(rule: RuleLike): ReadonlyArray<CompanyFeaturesEnum> {
  return Object.entries(FEATURE_PREDICATES)
    .filter(([, predicate]) => predicate(rule))
    .map(([feature]) => feature as CompanyFeaturesEnum);
}

function isAdvancedForCompany(hasUpgradedRules: boolean, rule: RuleLike) {
  if (hasUpgradedRules) {
    return (
      containsAdvancedEventsPre2021(rule) ||
      containsAdvancedActionsPre2021(rule) ||
      containsAdvancedCompoundConditionsPre2021(rule)
    );
  }

  return (
    containsAdvancedEvents(rule) || containsAdvancedActions(rule) || containsAdvancedCompoundConditions(rule)
  );
}

/** For legacy companies with upgraded_rules, fewer conditions are considered advanced. */
function containsAdvancedCompoundConditionsPre2021(rule: RuleLike) {
  const aggActionsType = reduceCompoundConditions(rule);

  const isBasicPre2021 = aggActionsType.some((elem) => upgradedRules2021.compoundConditions.includes(elem));

  return !isBasicPre2021 && aggActionsType.some((elem) => advancedRules.compoundConditions.includes(elem));
}

function containsAdvancedEventsPre2021(rule: RuleLike) {
  const isBasicPre2021 = rule.events.some((elem) => upgradedRules2021.events.includes(elem));

  return !isBasicPre2021 && containsAdvancedEvents(rule);
}

function containsAdvancedActions(rule: RuleLike) {
  const aggActionsType = rule.actions.map((action) => action.type);

  return aggActionsType.some((elem) => advancedRules.actions.includes(elem));
}

function containsAccountActions(rule: RuleLike) {
  const aggActionsType = rule.actions.map((action) => action.type);
  return aggActionsType.some((elem) => accountRules.actions.includes(elem));
}

function containsAccountConditions(rule: RuleLike) {
  return reduceCompoundConditions(rule).some((condition) =>
    accountRules[RulesNamespaceTypesEnum.COMPOUND_CONDITIONS].includes(condition),
  );
}

/** For legacy companies with upgraded_rules, fewer actions are considered advanced. */
function containsAdvancedActionsPre2021(rule: RuleLike) {
  const aggActionsType = rule.actions.map((action) => action.type);

  const isBasicPre2021 = aggActionsType.some((elem) => upgradedRules2021.actions.includes(elem));

  return !isBasicPre2021 && containsAdvancedActions(rule);
}

function containsAdvancedEvents(rule: RuleLike) {
  return rule.events.some((elem) => advancedRules.events.includes(elem));
}

function containsAdvancedCompoundConditions(rule: RuleLike) {
  const aggActionsType = reduceCompoundConditions(rule);

  return aggActionsType.some((elem) => advancedRules.compoundConditions.includes(elem));
}

function containsRuleExpressions(rule: RuleLike) {
  return Boolean(rule.expressions?.length);
}

function reduceCompoundConditions(rule: RuleLike) {
  // We only want to keep object or array.
  // We also want to flatten the array to ease the process later on.

  /*
   *  A RuleModel contains a 'compound_conditions' item, while a 'RuleRecipeModel'
   *  contains a rule item that contains a 'conditions' item. Other than the
   *  'compound_conditions' vs. 'conditions' keyname, these are otherwise identical
   *  items of the type 'RuleCompoundConditions'. We can operate on both recipies
   *  and rules as long as we make sure we are looking for the correct keyname.
   */

  const reducedCompoundConditions = rule.compoundConditions
    .filter((condition) => typeof condition === 'object')
    .map((condition) => (!Array.isArray(condition) ? [condition] : condition))
    .reduce((acc, current) => [...acc, ...current], []);

  return reducedCompoundConditions
    .map((condition) => (typeof condition === 'string' ? undefined : condition.type))
    .filter(isDefined);
}

export function parseApiError(err: any) {
  // Ignore transition errors.
  if (!err || err.message === 'transition prevented') {
    return null;
  }

  return err.data?.message || err.statusText || <Trans id="rules-error-occurred">An error occurred</Trans>;
}

export function getRulesRoute(namespace: string) {
  return `/settings/${namespace}/rules`;
}

export function trackRuleEvent(
  eventName: InterfaceEventsEnum,
  rule: Partial<RuleBaseModel> | undefined,
  namespace: string,
  recipeId?: string,
  additionalData: Record<string, unknown> = {},
) {
  return trackInterfaceEvent(eventName, {
    rule_id: rule?.id ?? null,
    recipe_id: recipeId ?? 'custom_rule',
    settings_subpage: getSettingsSubpage(eventName),
    settings_page: 'rules',
    settings_type: getSettingsType(namespace),
    ...additionalData,
  });
}

function getSettingsSubpage(eventName: InterfaceEventsEnum) {
  switch (eventName) {
    case InterfaceEventsEnum.SETTINGS_RULE_CREATE:
      return 'Add a rule';

    case InterfaceEventsEnum.SETTINGS_RULE_UPDATE:
    case InterfaceEventsEnum.SETTINGS_RULE_DELETE:
    case InterfaceEventsEnum.SETTINGS_RULE_VIEW:
      return 'Edit an existing rule';

    default:
      return undefined;
  }
}

export function getSettingsType(namespace: string) {
  if (computeIsGlobalNamespace(namespace)) {
    return 'company';
  }

  if (isTeamNamespace(namespace)) {
    return 'team';
  }

  return 'personal';
}

export function hasUnrestrictedInboxScope(rule: RuleModelStrict) {
  if (rule.bundle.type === RuleBundleTypesEnum.CUSTOM_VIEW) {
    return false;
  }

  // Find if there is a rule in the bundle with a team scope while having no inbox filters on any rules of that bundle.
  const namespaces = rule.footprintNamespaces;
  const hasTeamNamespace = namespaces.some((n) => n.startsWith('tim'));
  const hasInboxNamespace = namespaces.some((n) => n.startsWith('inb'));
  return hasTeamNamespace && !hasInboxNamespace;
}

export function getAvailableNamespaces(
  namespace: string,
  user: TeammateModel,
  teammates: ReadonlyArray<TeammateModel>,
): ReadonlyArray<string> {
  if (isTeamNamespace(namespace)) {
    return [namespace];
  }

  if (computeIsGlobalNamespace(namespace)) {
    return Object.keys(user.access).filter(isTeamNamespace);
  }

  // If the namespace is teammate, get accessible namespaces of this teammate.
  const computeHasMatchingNamespace = (teammate: TeammateModel) => teammate.namespace === namespace;
  const teammate = teammates.find(computeHasMatchingNamespace);
  if (!teammate) {
    return [];
  }

  return Object.keys(teammate.access).filter((n) => !computeIsGlobalNamespace(n));
}
